import React, { useState, forwardRef } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Box,
    Avatar,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";
import { categoryNamespaces } from "../../../../Helpers/CategoryNamespaces";
import ProductStatusChip from "../../../../Components/ProductStatusChip";
import {
    PRODUCT_STATUS,
    getProductStatus,
    needsPhotoToPublish,
    isPublishableDraft,
} from "../../../../Constants/productStatus";
import { textActionButtonStyle } from "./styles";

const tableHeaderStyles = {
    textTransform: "capitalize",
    color: "rgba(62, 63, 76, 0.75)",
    fontSize: "14px",
    fontWeight: 400,
};

const tableCellStyles = {
    color: "#3E3F4C",
    borderBottom: "1px solid #EAEAEA",
    padding: "12px 16px",
    fontSize: "15px",
    fontWeight: 400,
};

const kebabMenuSx = {
    "& .MuiPaper-root": {
        borderRadius: "8px",
        boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.12)",
        minWidth: "160px",
    },
    "& .MuiMenuItem-root": {
        fontSize: "14px",
        fontWeight: 400,
        color: "#2F2B3D",
        px: "16px",
        py: "10px",
        "&:hover": {
            backgroundColor: "#E1F0ED",
        },
    },
};

const ProductsTable = forwardRef(({
    products,
    onEdit,
    onDuplicate,
    onDelete,
    onPauseToggle,
    onAddPhoto,
    onPublish,
    vertical = "products",
}, ref) => {
    const categoryNs = categoryNamespaces(vertical);
    const { t } = useTranslation(["supplier-products", ...categoryNs]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [activeProduct, setActiveProduct] = useState(null);

    const handleMenuOpen = (event, product) => {
        setAnchorEl(event.currentTarget);
        setActiveProduct(product);
    };

    const activeStatus = activeProduct ? getProductStatus(activeProduct) : null;

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action) => {
        if (!activeProduct) return;
        switch (action) {
            case "edit":
                onEdit(activeProduct);
                break;
            case "duplicate":
                onDuplicate(activeProduct);
                break;
            case "pause":
                onPauseToggle(activeProduct);
                break;
            case "add_photo":
                onAddPhoto(activeProduct);
                break;
            case "publish":
                onPublish(activeProduct);
                break;
            case "delete":
                onDelete(activeProduct);
                break;
            default:
                break;
        }
        handleMenuClose();
    };

    const currencySymbols = {
        ILS: '₪',
        USD: '$',
        EUR: '€',
    };

    const formatPrice = (price, currency) => {
        if (price == null) return "-";
        const num = Number(price);
        const symbol = currencySymbols[currency] || currency || '₪';
        return isNaN(num)
            ? "-"
            : `${symbol}${num.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            })}`;
    };

    return (
        <TableContainer
            ref={ref}
            sx={{
                borderRadius: "16px",
                backgroundColor: "#fff",
                // No own vertical scroll / height cap: the parent content box
                // owns the scroll, so the table grows naturally and only one
                // scrollbar shows. The sticky header below sticks to that box.
                overflowX: "auto",
            }}
        >
            <Table>
                <TableHead
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        backgroundColor: "#fff",
                    }}
                >
                    <TableRow>
                        <TableCell sx={tableHeaderStyles}>
                            {t("table_columns.product_id")}
                        </TableCell>
                        <TableCell sx={tableHeaderStyles}>
                            {t("table_columns.product_name")}
                        </TableCell>
                        <TableCell sx={tableHeaderStyles}>
                            {t("table_columns.category")}
                        </TableCell>
                        <TableCell sx={tableHeaderStyles}>
                            {t("table_columns.price")}
                        </TableCell>
                        <TableCell sx={tableHeaderStyles}>
                            {t("table_columns.private_sku")}
                        </TableCell>
                        <TableCell sx={{ ...tableHeaderStyles, width: "48px" }} />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {products.map((product, index) => (
                        <TableRow
                            key={product.id + "-" + index}
                            sx={{
                                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.02)" },
                            }}
                        >
                            <TableCell sx={tableCellStyles}>{product.id}</TableCell>
                            <TableCell sx={tableCellStyles}>
                                <Box display="flex" alignItems="center" gap="10px">
                                    <Avatar
                                        variant="rounded"
                                        src={product.main_image_url}
                                        alt={product.name}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: "6px",
                                            fontSize: "13px",
                                            bgcolor: "#E8E8E8",
                                        }}
                                    >
                                        {product.name?.charAt(0)}
                                    </Avatar>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: "2px",
                                            minWidth: 0,
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap="10px">
                                            <Typography
                                                sx={{
                                                    fontSize: "15px",
                                                    fontWeight: 400,
                                                    color: "#3E3F4C",
                                                    maxWidth: "200px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {product.name}
                                            </Typography>
                                            <ProductStatusChip product={product} />
                                        </Box>
                                        {/* A draft or a paused item with no
                                            photo can't go live until one is
                                            added: say so where the seller
                                            looks, and take them there. */}
                                        {needsPhotoToPublish(product) && (
                                            <Box
                                                component="button"
                                                type="button"
                                                onClick={() => onAddPhoto(product)}
                                                sx={textActionButtonStyle}
                                            >
                                                {t("draft.add_photo_cta")}
                                            </Box>
                                        )}
                                        {/* A draft that already has its photo
                                            is one click from going live. */}
                                        {isPublishableDraft(product) && (
                                            <Box
                                                component="button"
                                                type="button"
                                                onClick={() => onPublish(product)}
                                                sx={textActionButtonStyle}
                                            >
                                                {t("draft.publish_cta")}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell sx={tableCellStyles}>
                                {product.category_name
                                    ? `${t(product.category_name, { ns: categoryNs, defaultValue: product.category_name })}${product.subcategory_name
                                        ? ` - ${t(product.subcategory_name, { ns: categoryNs, defaultValue: product.subcategory_name })}`
                                        : ""
                                    }`
                                    : "-"}
                            </TableCell>
                            <TableCell sx={tableCellStyles}>
                                {formatPrice(product.price, product.currency)}
                            </TableCell>
                            <TableCell sx={tableCellStyles}>
                                {product.private_sku || t("na")}
                            </TableCell>
                            <TableCell sx={{ ...tableCellStyles, width: "48px" }}>
                                <IconButton
                                    size="small"
                                    onClick={(e) => handleMenuOpen(e, product)}
                                >
                                    <MoreVertIcon sx={{ fontSize: "20px", color: "#6C727F" }} />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Kebab menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                sx={kebabMenuSx}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                {needsPhotoToPublish(activeProduct) && (
                    <MenuItem onClick={() => handleAction("add_photo")}>
                        {t("kebab_menu.add_photo_and_publish")}
                    </MenuItem>
                )}
                {isPublishableDraft(activeProduct) && (
                    <MenuItem onClick={() => handleAction("publish")}>
                        {t("kebab_menu.publish")}
                    </MenuItem>
                )}
                <MenuItem onClick={() => handleAction("edit")}>
                    {t("kebab_menu.edit_product")}
                </MenuItem>
                <MenuItem onClick={() => handleAction("duplicate")}>
                    {t("kebab_menu.duplicate_product")}
                </MenuItem>
                {/* A draft is already hidden from members, so it has nothing
                    to pause. A paused item without a photo can't be unpaused
                    (the server refuses it), so "Add photo and publish" above
                    takes its place. */}
                {activeStatus === PRODUCT_STATUS.ACTIVE && (
                    <MenuItem onClick={() => handleAction("pause")}>
                        {t("kebab_menu.pause_product")}
                    </MenuItem>
                )}
                {activeStatus === PRODUCT_STATUS.PAUSED &&
                    !needsPhotoToPublish(activeProduct) && (
                        <MenuItem onClick={() => handleAction("pause")}>
                            {t("kebab_menu.unpause_product")}
                        </MenuItem>
                    )}
                <MenuItem onClick={() => handleAction("delete")}>
                    {t("kebab_menu.delete_product")}
                </MenuItem>
            </Menu>
        </TableContainer>
    );
});

export default ProductsTable;
